import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { Box, Button, List, ListItem, ListItemButton, ListItemText, Tooltip } from '@unity/cloud-ui';
import { TocEntry } from '@stefanprobst/rehype-extract-toc';
import { useTranslations } from 'next-intl';
import { getAnchorLink } from '@/utils/get-anchor-link';
import { scrollToAnchor } from '@/utils/scroll-to-anchor';
import { CUSTOM_HEADING_IDENTIFIER } from '../content-renderer/mdx-components/header/header';

export function TableOfContentListItem(props: {
  item: TocEntry;
  selectedItemId: string | null;
  handleItemClick(id: string): void;
}) {
  const t = useTranslations();
  const [expanded, setExpanded] = useState(true);
  const hasChildren = !!props.item.children && props.item.children.length > 0;
  const [label, customId] = props.item.value.split(CUSTOM_HEADING_IDENTIFIER);
  const anchorName = customId || getAnchorLink(label);
  const isSelected = anchorName === props.selectedItemId;
  const listId = `toc-list-${anchorName}`;

  function handleOnIconClick(e: React.MouseEvent<HTMLElement, MouseEvent>) {
    // Prevent clicking on the actual toc item
    e.stopPropagation();
    e.preventDefault();

    setExpanded((prev) => !prev);
  }

  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    scrollToAnchor(anchorName, { behavior: 'smooth' });
    props.handleItemClick(anchorName);
    if (window.location.hash !== `#${anchorName}`) {
      window.location.hash = anchorName;
    }
  }

  return (
    <>
      <ListItem
        key={anchorName}
        aria-level={props.item.depth}
        id={anchorName}
        sx={{
          width: 'auto',
          p: 0,
          ml: (props.item.depth - 1) * 1.25 - 1.5,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Tooltip title={label} enterDelay={800} placement="auto">
          <ListItemButton
            selected={isSelected}
            sx={{
              py: 0.5,
              px: 1,
              '&.Mui-selected:hover': {
                backgroundColor: (theme) => `${theme.palette.action.selected} !important`,
              },
              flex: 1,
            }}
            href={`#${anchorName}`}
            onClick={handleClick}
            aria-current={isSelected ? 'page' : undefined}
            aria-expanded={hasChildren ? expanded : undefined}
            aria-controls={hasChildren ? listId : undefined}
          >
            <ListItemText
              primary={label}
              sx={{ m: 0, pr: hasChildren ? 3 : 0 }}
              slotProps={{
                primary: {
                  variant: 'body2',
                  sx: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '&::after': {
                      /* Removes the safari tooltip */
                      content: '""',
                      display: 'block',
                    },
                  },
                },
              }}
            />
          </ListItemButton>
        </Tooltip>
        {hasChildren && (
          <Button
            color="default"
            onClick={handleOnIconClick}
            sx={(theme) => ({
              position: 'absolute',
              right: theme.spacing(1),
              p: 0.5,
            })}
            aria-label={expanded ? t('accessibility:collapse-section') : t('accessibility:expand-section')}
            tabIndex={0}
          >
            <Box component={expanded ? CaretDownIcon : CaretRightIcon} size={16} />
          </Button>
        )}
      </ListItem>

      {hasChildren && (
        <li>
          <List
            dense
            id={listId}
            sx={{
              p: 0,
              display: expanded ? 'block' : 'none',
            }}
          >
            {props.item.children?.map((child, index) => (
              <TableOfContentListItem
                key={index}
                item={child}
                selectedItemId={props.selectedItemId}
                handleItemClick={props.handleItemClick}
              />
            ))}
          </List>
        </li>
      )}
    </>
  );
}
